import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import apiClient from "../../business/network/ApiClient";
import { CurrentUser } from "../../business/helper/CurrentUser";

/**
 * AgentAccessContext — which agents this user may see (design §15.8).
 *
 * The client never decides entitlement. It renders whatever `GET /Agents/Mine` returns, so
 * there is no hard-coded agent list anywhere in the frontend, and shipping agent #2 is a
 * registry row, a grant, and a module registered by key — not a frontend refactor.
 *
 * Cached per session and refreshed on demand: a revoked grant makes the next API call 404,
 * and the rail entry disappears on the next refresh (§17).
 *
 * <b>Resolving is retried, and a failure is never mistaken for an empty entitlement.</b> The
 * rail is hidden when a user has no agents, so anything that answered "no agents" wrongly —
 * a token that had not landed in localStorage yet, a backend restarting, a 401 during a
 * refresh — removed the whole navigation for the rest of the session with nothing to click.
 */
const AgentAccessContext = createContext(null);

/**
 * Backoff between attempts, in ms — one entry per retry, so the first call plus these is six
 * tries over about 15 seconds. Long enough to ride out a backend restart, and it only ever runs
 * when something is actually wrong: a genuine empty entitlement resolves on the first call.
 */
const RETRY_DELAYS = [400, 1000, 2000, 4000, 8000];

/** After the backoff, the quiet cadence it settles into for the life of the tab. */
const IDLE_RETRY_MS = 30000;

export const AgentAccessProvider = ({ children }) => {
  const [agents, setAgents] = useState(null); // null = not resolved yet, [] = none
  const [isLoading, setIsLoading] = useState(true);
  const [hasFailed, setHasFailed] = useState(false);

  const timerRef = useRef(null);
  const attemptRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearTimeout(timerRef.current);
    };
  }, []);

  const resolve = useCallback(async () => {
    const user = CurrentUser.GetCurrentUserData();

    // No user data yet is "not yet", not "no agents": this provider can mount before the token
    // has been written, on a cold start or a redirect in from Central.
    const list = user?.ouId ? await apiClient.getMyAgents(user.ouId, user.userId) : null;

    if (!isMountedRef.current) return null;

    if (list === null) {
      attemptRef.current += 1;
      const inBackoff = attemptRef.current <= RETRY_DELAYS.length;

      if (!inBackoff) {
        // Stop *waiting* on it — the chat is entirely usable without a rail — but keep the
        // agents we already had rather than emptying a rail that worked a moment ago.
        setHasFailed(true);
        setAgents((previous) => previous ?? []);
        setIsLoading(false);
      }

      // And keep asking, quietly, for as long as the tab is open. One small request every 30s
      // is the difference between the menu coming back by itself when the backend answers
      // again, and the user having to know to reload.
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(
        resolve,
        inBackoff ? RETRY_DELAYS[attemptRef.current - 1] : IDLE_RETRY_MS
      );
      return null;
    }

    attemptRef.current = 0;
    setHasFailed(false);
    setAgents([...list].sort((a, b) => (a.sortOrder ?? 100) - (b.sortOrder ?? 100)));
    setIsLoading(false);
    return list;
  }, []);

  const refresh = useCallback(() => {
    clearTimeout(timerRef.current);
    attemptRef.current = 0;
    setIsLoading(true);
    return resolve();
  }, [resolve]);

  useEffect(() => {
    resolve();
  }, [resolve]);

  const value = useMemo(
    () => ({
      agents: agents || [],
      isLoading,
      hasFailed,
      refresh,
      hasAgent: (agentKey) => (agents || []).some((a) => a.agentKey === agentKey),
    }),
    [agents, isLoading, hasFailed, refresh]
  );

  return (
    <AgentAccessContext.Provider value={value}>{children}</AgentAccessContext.Provider>
  );
};

export const useAgentAccess = () => {
  const context = useContext(AgentAccessContext);
  if (!context) {
    throw new Error("useAgentAccess must be used inside an AgentAccessProvider");
  }
  return context;
};

export default AgentAccessContext;
